import { colorStrings } from "@iewcasia/Utilities/variables";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentUserIsGuest, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import loadBillTo from "@insite/client-framework/Store/Data/BillTos/Handlers/LoadBillTo";
import { getCartState, getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import loadOrderByOrderNumber from "@insite/client-framework/Store/Data/Orders/Handlers/LoadOrderByOrderNumber";
import { getOrderState } from "@insite/client-framework/Store/Data/Orders/OrdersSelectors";
import loadShipTo from "@insite/client-framework/Store/Data/ShipTos/Handlers/LoadShipTo";
import { getShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import { getHomePageUrl, getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import React, { useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const cartState = getCurrentCartState(state);
    const accountSettings = getSettingsCollection(state).accountSettings;
    return {
        cartState,
        billToState: getBillToState(state, cartState.value?.billToId),
        shipToState: getShipToState(state, cartState.value?.shipToId),
        orderState: getOrderState(state, cartState.value?.orderNumber),
        homePageUrl: getHomePageUrl(state),
        currentUserIsGuest: getCurrentUserIsGuest(state),
        allowGuestRFQCheckout: accountSettings.allowGuestCheckout,
        rfqRequestQuoteGuestPageUrl: getPageLinkByPageType(state, "RfqRequestQuoteGuestPage")?.url,
        isGuest: state.context.session?.isGuest,
        rfqRequestQuotePageUrl: getPageLinkByPageType(state, "RfqRequestQuotePage")?.url,
    };
};

const mapDispatchToProps = {
    loadBillTo,
    loadShipTo,
    loadOrderByOrderNumber,
};

export interface OrderConfirmationOrderInformationStyles {
    OrderInformationGridContainer?: GridContainerProps;
    orderNumberGridItem?: GridItemProps;
    orderNumberHeading?: TypographyProps;
    addToListButton?: ButtonPresentationProps;
    rfqButton?: ButtonPresentationProps;
}

export const orderInformationStyles: OrderConfirmationOrderInformationStyles = {
    OrderInformationGridContainer: {
        gap: 10,
        css: css`
            background-color: ${getColor(colorStrings.commonGlobalWhite)};
            padding: 10px;
            border: 1px solid ${getColor(colorStrings.commonBorder)};
        `,
    },
    orderNumberGridItem: {
        width: 12,
    },
    orderNumberHeading: {
        variant: "p",
        as: "p",
        weight: "bold",
        css: css`
            margin-bottom: 5px;
        `,
    },

    addToListButton: {
        css: css`
            margin-right: 10px;
        `,
    },
};

type Props = HasHistory & WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const styles = orderInformationStyles;

const OrderConfirmationOrderInformation = ({
    cartState,
    billToState,
    shipToState,
    orderState,
    loadBillTo,
    loadShipTo,
    loadOrderByOrderNumber,
    history,
    homePageUrl,
    currentUserIsGuest,
    allowGuestRFQCheckout,
    rfqRequestQuotePageUrl,
    rfqRequestQuoteGuestPageUrl,
}: Props) => {
    useEffect(() => {
        if (cartState.isLoading || !cartState.value) {
            return;
        }

        const cart = cartState.value;
        if (!billToState.isLoading && !billToState.value && cart.billToId) {
            loadBillTo({ billToId: cart.billToId });
        }

        if (!shipToState.isLoading && !shipToState.value && cart.billToId && cart.shipToId) {
            loadShipTo({ billToId: cart.billToId, shipToId: cart.shipToId });
        }

        if (!orderState.isLoading && !orderState.value && cart.orderNumber && cart.status === "Submitted") {
            loadOrderByOrderNumber({ orderNumber: cart.orderNumber });
        }
    }, [cartState]);

    const cart = cartState.value;
    const billTo = billToState.value;
    const shipTo = shipToState.value;
    if (!cart || !billTo || !shipTo || (cart.canRequestQuote && cart.quoteRequiredCount === 0)) {
        return null;
    }

    const continueClickHandler = () => {
        history.push(homePageUrl);
    };

    const rfqClickHandler = () => {
        if (currentUserIsGuest && allowGuestRFQCheckout && rfqRequestQuoteGuestPageUrl) {
            history.push(`${rfqRequestQuoteGuestPageUrl}?cartId=${cart.id}`);
            return;
        }

        if (rfqRequestQuotePageUrl) {
            history.push(`${rfqRequestQuotePageUrl}?cartId=${cart.id}`);
        }
    };

    return (
        <GridContainer {...styles.OrderInformationGridContainer}>
            <GridItem {...styles.orderNumberGridItem}>
                <Typography {...styles.orderNumberHeading}>
                    {siteMessage("Rfq_Requires_Quote_InCartMessage")}
                </Typography>
            </GridItem>
            <GridItem {...styles.orderNumberGridItem}>
                <Button {...styles.addToListButton} onClick={rfqClickHandler}>
                    {translate("Yes, Request a Quote")}
                </Button>
                <Button {...styles.rfqButton} onClick={continueClickHandler} className="verndaleGA4__continue-shopping">
                    {translate("No, Continue Shopping")}
                </Button>
            </GridItem>
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(OrderConfirmationOrderInformation)),
    definition: {
        displayName: "Order RFQ Message",
        allowedContexts: ["OrderConfirmationPage"],
        group: "Order Confirmation",
    },
};

export default widgetModule;
